<template>
  <!-- crossorigin is needed to set request.mode="cors" , without which
    the serviceworker will see an opaque response which is dangerous to
    cache. See: #7397

    Any changes to this attribute should be kept in sync with the article
    card, and also the preload code in `_loadImagesForDocs` in the
    model.
  -->
  <div class="book-cover">
    <component
      :is="lazyComponent"
      v-if="doc.cover_image"
      :src="coverUrl"
      crossorigin="anonymous"
      class="book-cover__image"
      :class="humanCoverClass"
      :sizes="sizes"
      :srcset="srcset"
      alt=""
    />
    <BookFallbackCover
      :title="doc.title || doc.doc_title"
      :subtitle="doc.author"
      :class="fallbackClass"
    />
  </div>
</template>
<script setup lang="ts">
import { computed } from 'vue'

import { Doc } from '@/models/interfaces'
import { cacheBust as addCacheBust } from '@/models/doc'

import BookFallbackCover from './BookFallbackCover.vue'
import { IS_PRERENDERING } from '@/init/settings'
import LazyImage from '@/app/components/podcast/card/LazyImage.vue'
import PublicImage from '@/app/components/podcast/card/PublicImage.vue'
import { useFallbackCover_13860 } from '@/services/ab'
import { isContentTranslated } from '@/services/weglot'

const props = defineProps<{
  doc: Doc
  coverClass?: string
  fallbackCoverClass?: string
  cacheBust?: boolean
  sizes?: string
  srcset?: string
}>()

const useFallbackCover = computed(() => {
  return isContentTranslated() || useFallbackCover_13860(props.doc)
})

const humanCoverClass = computed(() => {
  const classes = [props.coverClass]

  if (IS_PRERENDERING) {
    // We don't check for is_first_doc on public pages
    return classes
  }

  // Add hide_cover_13860 if we variant B will be applied
  if (useFallbackCover.value) {
    classes.push('hide_cover_13860')
  }

  return classes
})

const fallbackClass = computed(() => {
  const classes = [props.fallbackCoverClass]

  // Always show fallback if we don't have a cover_image
  if (!props.doc.cover_image) {
    return classes
  }

  // Hide fallback cover during prerendering or if we want to show the human cover.
  if (IS_PRERENDERING || !useFallbackCover.value) {
    classes.push('hide_cover_13860')
    return classes
  }

  // Otherwise, do not hide the fallback cover (human cover should be hidden
  // at the same time by the logic in the `humanCoverClass` property)
  return classes
})

const coverUrl = computed(() => {
  if (!props.doc.cover_image) {
    return
  }

  if (props.cacheBust) {
    return addCacheBust(props.doc.cover_image)
  }

  return props.doc.cover_image
})

const lazyComponent = computed(() => {
  return !IS_PRERENDERING ? LazyImage : PublicImage
})
</script>

<style>
.hide_cover_13860 {
  display: none !important;
}

.opacity_cover_13860 {
  opacity: 0;
}

.book-cover {
  flex-shrink: 0;
}
</style>
